/**
 * Who decided a decision, and why, in words an operator can act on.
 *
 * THE DEFECT THIS EXISTS FOR. The page showed the lane and called it the
 * authority: "Authority: dcs, Basis: authority_unavailable", on a row where
 * nobody at DCS was involved and Service OCPI never answered. So a card DCS
 * refused, a public card refused by Service OCPI's own work-charger rule, and
 * an authorization nothing answered at all were three different events reading
 * the same (FIND-43).
 *
 * `authority` is the lane and stays the lane: the Hub feed and the lane verdict
 * are defined over it (DEC-17). `decided_by` is who decided. This module is the
 * only place that difference is turned into English, so the list and the drawer
 * cannot disagree about it.
 *
 * THE VOCABULARY IS NOT OURS. Service OCPI owns the reason codes and the
 * decider names, and may add to either without a release here. Anything
 * unrecognised is shown verbatim rather than hidden or bucketed: a word we do
 * not know is still the best thing on the page for whoever has to ask them
 * about it, and translating it into "other" would lose the only evidence.
 *
 * NULL IS NOT A VALUE. A decision taken before these fields existed, or by a
 * Service OCPI that does not send them yet, says "not recorded". It never says
 * "DCS" and never says "DCS was not called", because nobody said either.
 */

import type { DecisionSummary } from '../api'

/** The deciders this console has sentences for. Theirs, plus our own two. */
export const KNOWN_DECIDERS = [
  'wall-e',
  'dcs',
  'hubject',
  'eloaded-ptp',
  'nobody',
  'tim',
  'eas',
  'wall_e_list',
] as const

const DECIDER_LABELS: Record<string, string> = {
  'wall-e': 'Service OCPI',
  dcs: 'DCS',
  hubject: 'Hubject',
  // Service OCPI's answer for an alias an eLoaded card terminal issued for one
  // order (DEC-27). The terminal took the payment; nobody else was asked.
  'eloaded-ptp': 'eLoaded terminal',
  // Not a decider's name: it is Service OCPI saying no list it holds has the
  // card. It read "nobody was asked", beside a sentence saying Service OCPI
  // was asked, which is the riddle Lucian asked to be rid of (2026-10-17).
  nobody: 'no list held it',
  tim: 'TIM',
  // Ours. Every fail-closed path and every policy denial lands here, and
  // calling it DCS is the mislabel this module exists to remove (DEC-13).
  eas: 'this service',
  // The tokens this service decides itself, asking nobody (DEC-28).
  wall_e_list: 'Wall-E list',
}

/** What to put in the "Decided by" cell. */
export function decidedByLabel(decidedBy: string | null | undefined): string {
  if (!decidedBy) return 'not recorded'
  return DECIDER_LABELS[decidedBy] ?? decidedBy
}

/**
 * Service OCPI's reason codes in plain English.
 *
 * Their list, from their own code: the ones that carry a decision are spelled
 * out, and the eroaming_* family is prefixed rather than enumerated because it
 * grows on their side.
 */
const REASONS: Record<string, string> = {
  work_charger_rejected: 'a public card at a work charger',
  token_revoked: 'the card is revoked',
  dcs_rejected: 'DCS refused the card',
  dcs_unknown_token: 'DCS does not know the card',
  dcs_timeout: 'DCS did not answer in time',
  token_not_found: 'the card is in no list',
  dcs_realtime: 'DCS answered in real time',
  local_cache: 'from the local list, without asking DCS',
  offline_fallback: 'the offline fallback decided it',
  internal_error: 'Service OCPI failed inside itself',
  // eLoaded card payment (DEC-27). Service OCPI decides an alias itself, with
  // decidedBy eloaded-ptp, and never answers UNKNOWN for one.
  ptp_alias_matched: 'it is an eLoaded payment code, paid for this station',
  ptp_alias_refused:
    'the eLoaded payment code is expired, already used, for another station, '
    + 'or card payment is switched off',
  // TIM, from their 86b7d02. NOT "no grade could be assigned", which is what
  // the word looks like and what I read it as: `fuel_code` None means this
  // service did not know whether the connector is AC or DC, so TIM decided
  // from its whitelist on the union of the card's grades and skipped the
  // grade test. It appears on approvals and on refusals alike, because only
  // the grade check was skipped.
  //
  // It cannot appear before DEC-22 step 5, which is when this service starts
  // sending an explicit null for current_type. When it does appear, its count
  // is the size of the catalogue's gap as TIM experienced it.
  local_whitelist_no_grade:
    'decided from the whitelist without checking the fuel grade, because this '
    + 'service did not know whether the connector is AC or DC',
}

/** The prefix Service OCPI puts before the OCPI status DCS answered with. */
const DCS_ERROR = 'DCS_ERROR_'

/**
 * How a DCS call failed, in the two positions the sentence needs it.
 *
 * `asked` opens a sentence, `beside` follows a reason. Both are written out
 * rather than derived from each other: "DCS was asked and did not answer in
 * time" and "refused, and DCS did not answer in time" are the same fact and
 * not the same string, and deriving one by trimming the other is the kind of
 * cleverness that breaks the day a value needs different wording.
 *
 * Null for anything else, INCLUDING a value this console has not seen, so an
 * unfamiliar one falls through to the generic handling rather than being
 * described by a sentence written for something else. Their vocabulary is an
 * example list in the contract and not an enum.
 */
function dcsFailureInWords(
  response: string | null | undefined,
): { asked: string; beside: string } | null {
  if (!response) return null
  if (response === 'TIMEOUT') {
    // Never worded as slowness: their DCS timeout is 1.0 s, nowhere near our
    // 3 s budget, so a reader must not conclude that we were the constraint.
    return {
      asked: 'DCS was asked and did not answer in time',
      beside: 'DCS did not answer in time',
    }
  }
  if (response === 'UNREACHABLE') {
    return { asked: 'DCS could not be reached', beside: 'DCS could not be reached' }
  }
  if (response.startsWith(DCS_ERROR)) {
    // The status is carried through rather than translated. This console does
    // not hold OCPI's status vocabulary, and inventing a phrase for a number
    // would state something nobody here established.
    const status = response.slice(DCS_ERROR.length)
    return {
      asked: `DCS answered with an error (status ${status})`,
      beside: `DCS answered with an error (status ${status})`,
    }
  }
  if (response === 'ERROR') {
    return { asked: 'the call to DCS failed', beside: 'the call to DCS failed' }
  }
  return null
}

function reasonInWords(reason: string): string {
  const known = REASONS[reason]
  if (known) return known
  if (reason.startsWith('eroaming_')) {
    // No colon of its own: the sentence already has one after the decider, and
    // "Hubject: eRoaming: hubject accepted" reads as two labels and no words.
    return `eRoaming ${reason.slice('eroaming_'.length).replace(/_/g, ' ')}`
  }
  return reason
}

// ── WHAT HAPPENED, IN THREE PLAIN SENTENCES ────────────────────────────────
//
// Lucian, 2026-10-17: the readings were riddles. He wants to read what
// happened: "the station sent «zero», it is not a card, DCS was not asked".
// So every decision reads as three sentences, always in this order:
//
//   what arrived: the token itself where it was kept, otherwise its kind;
//   who was asked, and what they answered, in their own words;
//   what this service answered, and why, in one sentence.
//
// Their words are quoted as they came, because they are what somebody repeats
// to Service OCPI or TIM. Nothing is hedged: where a fact was not recorded the
// sentence leaves it out rather than guessing.

/** An eMAID by shape, the same pattern the service classifies by. */
const EMAID = /^[A-Z]{2}-?[0-9A-Z]{3}-?C[0-9A-Z]{8}-?[0-9A-Z]?$/i

/** An eLoaded payment code: one per card payment, issued by Service OCPI. */
const PTP_ALIAS = /^PTP[0-9A-HJKMNP-TV-Z]{8}$/

/** The shape of a token, from the token itself: "4 letters", "5 digits". */
export function shapeOf(token: string): string {
  const n = token.length
  if (PTP_ALIAS.test(token)) return 'an eLoaded payment code'
  if (/^\d+$/.test(token)) return n === 1 ? '1 digit' : `${n} digits`
  if (/^[A-Za-z]+$/.test(token)) return n === 1 ? '1 letter' : `${n} letters`
  if (EMAID.test(token)) return 'an eMAID'
  if (/^[0-9A-Fa-f]+$/.test(token)) return `${n} hexadecimal characters`
  return `${n} characters`
}

/** What Service OCPI said the token is (`id_token_kind`), as a phrase. */
function kindPhrase(kind: string | null | undefined): string | null {
  if (!kind) return null
  if (kind === 'card_uid') return 'a card number'
  if (kind === 'placeholder') return 'a placeholder, not a card'
  if (kind === 'other') return 'not a card'
  return `what Service OCPI calls ${kind}`
}

/** The token, when it was kept and can be read. */
function tokenOf(decision: DecisionSummary): string | null {
  return decision.identifier_record === 'kept' && decision.identifier ? decision.identifier : null
}

const NOT_KEPT: Record<string, string> = {
  decided_before_tokens_were_kept:
    'The token was not kept: this was decided before this service kept tokens.',
  could_not_be_encrypted:
    'The token was not kept: encrypting it failed, and the authorization went on without it.',
  could_not_be_decrypted:
    'The token was kept and cannot be read: the key it was encrypted with has changed.',
}

/** First sentence: what arrived. */
export function whatArrived(decision: DecisionSummary): string {
  const kind = kindPhrase(decision.id_token_kind)
  const token = tokenOf(decision)
  if (token) return `The station sent "${token}", ${shapeOf(token)}${kind ? `, ${kind}` : ''}.`
  const why = NOT_KEPT[decision.identifier_record ?? ''] ?? 'The token was not kept.'
  return kind ? `${why} Service OCPI said it was ${kind}.` : why
}

/** Who the lane is, as the subject of a sentence. */
function laneName(authority: string | null | undefined): string {
  if (authority === 'dcs') return 'Service OCPI'
  if (authority === 'tim') return 'TIM'
  if (authority === 'hubject') return 'The Hubject connector'
  if (authority === 'wall_e_list') return 'The Wall-E list'
  return authority ?? 'Nobody'
}

const NOBODY_ASKED: Record<string, string> = {
  policy_denied: 'Nobody was asked: the routing policy refuses this scheme before any call.',
  estate_unknown: 'Nobody was asked: the charge point could not be placed in an estate.',
  not_asked_no_budget:
    'Nobody was asked: the time allowed was spent before the authority could be.',
  wall_e_list: 'Nobody was asked: the token is on the Wall-E list, which this service decides itself.',
}

/** How Service OCPI's answer was decided, after its own verdict. */
function decidedPhrase(decidedBy: string | null | undefined): string {
  // Its own rule, ours, and "nobody" (no list held the card, which its reason
  // already says) add nothing to "It answered X".
  if (!decidedBy || ['wall-e', 'eas', 'nobody'].includes(decidedBy)) return ''
  return `, decided by ${DECIDER_LABELS[decidedBy] ?? decidedBy}`
}

/** Second sentence or two: who was asked, and what they answered. */
export function whoWasAsked(decision: DecisionSummary): string {
  const { basis, outcome, reason } = decision
  if (NOBODY_ASKED[basis]) return NOBODY_ASKED[basis]
  if (!decision.authority) return 'Nobody was asked.'
  const name = laneName(decision.authority)
  if (basis === 'authority_unavailable') {
    if (decision.authority === 'dcs' && outcome === 'UNKNOWN') {
      return "Service OCPI was asked and refused this service's key, so it never saw the card."
    }
    return `${name} was asked and did not answer${reason ? `: ${reason}` : '.'}`
  }
  if (basis === 'authority_no_verdict') {
    return `${name} answered without a verdict${reason ? `: ${reason}` : '.'}`
  }
  if (basis === 'stamp_not_written') return `${name} was asked and answered APPROVED.`
  if (basis === 'authority_timed_out_upstream') {
    return 'The Hubject connector was asked and could not reach Hubject in time. It kept no session.'
  }
  if (basis === 'authority_deadline_passed') {
    return 'The Hubject connector was asked and had not answered when this service stopped waiting.'
  }
  if (basis === 'authority_accepted_no_session') {
    return 'The Hubject connector answered APPROVED and named no session.'
  }
  const said = reason ? `, saying "${reason}"` : ''
  if (decision.authority !== 'dcs') return `${name} was asked and answered ${outcome}${said}.`

  const dcs =
    decision.dcs_response === 'NOT_CONFIGURED'
      ? ' It could not ask DCS: its DCS step is not configured.'
      : decision.dcs_called === true
        ? ` It asked DCS, which answered ${decision.dcs_response ? `"${decision.dcs_response}"` : 'nothing that was recorded'}.`
        : decision.dcs_called === false
          ? decision.id_token_kind === 'placeholder' || decision.id_token_kind === 'other'
            ? ' It did not ask DCS, because this is not a card it asks DCS about.'
            : ' It did not ask DCS.'
          : ''
  return `Service OCPI was asked.${dcs} It answered ${outcome}${said}${decidedPhrase(decision.decided_by)}.`
}

/** What the answer amounted to, plainly, for "passing on their answer". */
function plainReason(decision: DecisionSummary): string | null {
  const token = tokenOf(decision)
  if (decision.reason === 'token_not_found' && decision.dcs_called === false) {
    if (decision.id_token_kind === 'placeholder') {
      return token ? `"${token}" is a placeholder, not a card` : 'the station sent a placeholder, not a card'
    }
    if (decision.id_token_kind === 'other') {
      return token ? `"${token}", ${shapeOf(token)}, is not a card` : 'what the station sent is not a card'
    }
    return 'no list it holds has the card, and DCS was not asked'
  }
  const dcsFailure = dcsFailureInWords(decision.dcs_response)
  if (decision.dcs_called && dcsFailure) {
    return decision.reason === 'token_not_found'
      ? `${dcsFailure.asked}, so nothing is known about the card`
      : `${decision.reason ? reasonInWords(decision.reason) : 'refused'}, and ${dcsFailure.beside}`
  }
  if (decision.reason === 'work_charger_rejected' && decision.dcs_response === 'ALLOWED') {
    return 'DCS accepted the card, and it was refused here as a work charger'
  }
  if (decision.decided_by === 'eloaded-ptp' && !decision.reason) {
    return 'an alias an eLoaded terminal issued for one payment'
  }
  return decision.reason ? reasonInWords(decision.reason) : null
}

const OUR_REASON: Record<string, string> = {
  policy_denied: 'because the routing policy refuses this scheme',
  estate_unknown: 'because a charge point that cannot be placed is refused',
  authority_unavailable: 'because nothing answered, and a refusal is the safe answer',
  authority_no_verdict: 'because the answer carried no verdict',
  stamp_not_written:
    'because the session could not be recorded here, so it could not have been billed',
  authority_timed_out_upstream: 'because the authority could not reach Hubject in time',
  authority_deadline_passed: 'because it stopped waiting before the authority answered',
  not_asked_no_budget: 'because there was no time left to ask',
  authority_accepted_no_session:
    'because a session it cannot name, it cannot start, stop or release',
}

/** The last sentence: what this service answered, and why. */
export function whatThisServiceAnswered(decision: DecisionSummary): string {
  const { basis, outcome } = decision
  if (basis === 'wall_e_list') {
    return outcome === 'APPROVED'
      ? 'This service answered APPROVED, because the token is accepted on the Wall-E list. No payer is named: the session goes to whoever approved a card at this station just before, or is kept here.'
      : 'This service answered BLOCKED, because the token is blocked on the Wall-E list.'
  }
  if (basis === 'authority_unavailable' && decision.authority === 'dcs' && outcome === 'UNKNOWN') {
    return `This service answered ${outcome}, because Service OCPI refused its key, so nothing is known about the card.`
  }
  const ours = OUR_REASON[basis]
  if (ours) return `This service answered ${outcome}, ${ours}.`
  if (decision.decided_by === 'eas') return `This service answered ${outcome}: ${basis}.`
  if (!decision.authority) return `This service answered ${outcome}.`
  const owner = decision.authority === 'dcs' ? "Service OCPI's" : `${laneName(decision.authority)}'s`
  const plain = plainReason(decision)
  return `This service answered ${outcome}, passing on ${owner.replace(/^The /, 'the ')} answer${plain ? `: ${plain}` : ''}.`
}

export interface Reading {
  arrived: string
  asked: string
  answered: string
}

/** The three sentences, for the drawer and the decision page. */
export function readingOf(decision: DecisionSummary): Reading {
  return {
    arrived: whatArrived(decision),
    asked: whoWasAsked(decision),
    answered: whatThisServiceAnswered(decision),
  }
}

/**
 * One short line for the table, where the decider has its own column.
 *
 * Concrete like the sentences: the token where it was kept, the shape, who
 * was not asked. A row a reader has to open to understand is a row the table
 * failed.
 */
export function reasonInWordsFor(decision: DecisionSummary): string {
  if (decision.dcs_response === 'NOT_CONFIGURED') {
    return 'Service OCPI could not ask DCS: its DCS step is not configured'
  }
  // The token itself is in the cell beside this one, so the line says its
  // shape and what that means, and the table stays readable.
  if (decision.reason === 'token_not_found' && decision.dcs_called === false) {
    const token = tokenOf(decision)
    const shape = token ? `${shapeOf(token)}, ` : ''
    if (decision.id_token_kind === 'placeholder') {
      return `${token ? shape : 'a placeholder, '}not a card, DCS was not asked`
    }
    if (decision.id_token_kind === 'other') return `${shape}not a card, DCS was not asked`
    return 'DCS was not asked, and Service OCPI gave no reason'
  }
  const ours = OUR_OWN[decision.basis]
  const oursToSay = decision.decided_by === 'eas' || (!decision.decided_by && !decision.reason)
  if (ours && (oursToSay || !decision.reason)) return ours
  // A basis with no sentence yet shows as itself: honest, not blank.
  if (oursToSay) return decision.basis
  return plainReason(decision) ?? ours ?? 'no reason was given'
}

/**
 * What this service decided by itself, per basis, for the table's short line.
 * Each points at a different repair: a key, a network, a policy, a cache.
 */
const OUR_OWN: Record<string, string> = {
  authority_unavailable: 'nothing answered, so this service refused',
  authority_no_verdict: 'the answer carried no verdict, so this service refused',
  policy_denied: 'refused by routing policy, before anyone was asked',
  estate_unknown: 'the charge point could not be placed, so this service refused',
  stamp_not_written:
    'approved, and this service could not record the session, so it refused',
  authority_answer: 'refused, and no reason was recorded',
  authority_timed_out_upstream:
    'the authority could not reach Hubject in time and kept no session, so this service refused',
  authority_deadline_passed:
    'this service stopped waiting, so whether the authority authorized this is unknown',
  not_asked_no_budget:
    'the time was spent before the authority could be asked, so nothing was sent',
  authority_accepted_no_session:
    'the authority approved and named no session, so this service could not use it',
  wall_e_list: 'decided from the Wall-E list, nobody was asked',
}

/**
 * The lane, labelled honestly.
 *
 * `dcs` in the column means "the default route, through Service OCPI", and it
 * is stored that way for every answer on that lane including the ones DCS never
 * saw. Showing the stored word made the page claim DCS answered. The stored
 * value is not changed: renaming it would break the Hub feed's vocabulary and
 * every historical row (DEC-17).
 */
export function laneLabel(authority: string | null | undefined): string {
  if (!authority) return 'no authority was called'
  if (authority === 'dcs') return 'Service OCPI'
  if (authority === 'tim') return 'TIM'
  if (authority === 'wall_e_list') return 'Wall-E list'
  return authority
}

/** Whether DCS was called, where null is nobody having said. */
export function dcsAskedInWords(called: boolean | null | undefined): string {
  if (called === true) return 'yes'
  if (called === false) return 'no'
  return 'not recorded'
}

/**
 * What DCS answered, for the drawer, where it sits under "DCS asked: yes".
 *
 * Their word is kept and a plain reading is put beside it, rather than
 * replacing it: the word is what somebody quotes to them, and the reading is
 * what the person in front of the page needs.
 */
/**
 * When Service OCPI started telling the four DCS failures apart.
 *
 * A CONSTANT, BECAUSE IT IS A HISTORICAL FACT AND CANNOT MOVE. My objection to
 * writing this a week ago was to a date nobody had set; this one is set, and
 * measured rather than relayed: `az containerapp revision show` gives
 * service-ocpi-prod--0000291, image service-ocpi:v0.3.176, created at this
 * instant, and their /api/health reports 0.3.176 with an uptime consistent
 * with it. The split arrived in their 0.3.170.
 *
 * IT IS AN UPPER BOUND AND IT ERRS TOWARDS WARNING. Only that one revision is
 * retained, so an earlier one may also have carried 0.3.170 or later and I
 * cannot tell. The error that leaves is a caveat shown on a row that was in
 * fact labelled correctly, which prompts a second look. It never hides the
 * caveat on a row that is genuinely ambiguous.
 *
 * The alternative was deriving it from the data, as the earliest row carrying
 * one of the new words. Not taken: it is a lower bound rather than an upper
 * one and equally safe, but it costs a request on every load of a hot page to
 * answer a question with one fixed answer, and it would be undefined until the
 * first DCS error ever occurred.
 */
const DCS_LABELS_SPLIT_AT = Date.parse('2026-10-06T20:07:30Z') / 1000

/**
 * Whether a TIMEOUT on this row might be their old catch-all rather than a
 * real timeout (FIND-46).
 *
 * Only TIMEOUT is ambiguous. UNREACHABLE and DCS_ERROR_ did not exist before
 * the split, so a row carrying one is necessarily after it, and a post-split
 * TIMEOUT means what it says.
 */
export function dcsTimeoutMayBeAnError(
  response: string | null | undefined,
  occurredAt: string | number | null | undefined,
): boolean {
  if (response !== 'TIMEOUT') return false
  const at = asEpochSeconds(occurredAt)
  // Unknown time means unknown side of the boundary. Not caveated: a sentence
  // saying "this may be a DCS error" on every row whose time could not be read
  // is noise on rows that are mostly recent, and the caveat is only worth
  // anything while it is rare enough to be read.
  return at !== null && at < DCS_LABELS_SPLIT_AT
}

/**
 * The row's time in epoch seconds, whichever of the two shapes it arrived in.
 *
 * `occurred_at` is typed `string | number` because it is both: the API
 * serialises a timestamptz as ISO, and parts of this console have carried
 * epoch seconds. Guessing one and casting the other is how a comparison
 * silently becomes false for every row of the shape nobody tested.
 */
function asEpochSeconds(value: string | number | null | undefined): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null
  if (typeof value !== 'string' || !value) return null
  const parsed = Date.parse(value)
  return Number.isNaN(parsed) ? null : parsed / 1000
}

export function dcsAnsweredInWords(
  response: string | null | undefined,
  occurredAt?: string | number | null,
): string {
  if (!response) return 'not recorded'
  // "TIMEOUT, WHICH IS THEIR WORD FOR NO ANSWER AT ALL" IS WHAT THIS SAID, and
  // it was their mislabel repeated here as a fact about DCS (FIND-46). Service
  // OCPI recorded every DCS failure other than an unknown token as TIMEOUT,
  // including calls DCS answered in 35 to 56 ms with status 3000. Their
  // ec244ab splits them, so the word now means what it says.
  if (response === 'TIMEOUT') {
    return dcsTimeoutMayBeAnError(response, occurredAt)
      ? 'TIMEOUT, so DCS did not answer in time. Recorded before Service OCPI '
        + 'told the four failures apart, so this one may have been a DCS error '
        + 'rather than a silence'
      : 'TIMEOUT, so DCS did not answer in time'
  }
  if (response === 'UNREACHABLE') return 'UNREACHABLE, so the connection to DCS failed'
  if (response.startsWith(DCS_ERROR)) {
    // Their status, carried through. This console does not hold OCPI's status
    // vocabulary, and a phrase invented for a number would state something
    // nobody here established; the number is what somebody takes to them.
    return `${response}, so DCS answered with OCPI status ${response.slice(DCS_ERROR.length)}`
  }
  if (response === 'ERROR') return 'ERROR, so the call to DCS failed'
  if (response === 'ALLOWED') return 'ALLOWED, so DCS accepted the card'
  if (response === 'UNKNOWN_TOKEN') return 'UNKNOWN_TOKEN, so DCS does not know the card'
  if (response === 'NOT_ALLOWED') return 'NOT_ALLOWED, so DCS refused the card'
  // Whatever else DCS put in `allowed`: BLOCKED, EXPIRED, NO_CREDIT, or one
  // added after this was written. Shown as it arrived.
  return response
}

/**
 * What arrived, for the drawer, in words rather than in their token.
 *
 * Shown on every row that has one, not only the odd ones: "a card number" is
 * the answer to "was this even a card", and a reader who only ever sees the
 * label on strange rows cannot tell a missing label from a normal one.
 */
export function identifierKindInWords(kind: string | null | undefined): string {
  if (!kind) return 'not recorded'
  if (kind === 'card_uid') return 'a card number'
  if (kind === 'placeholder') return 'a placeholder, not a card number'
  if (kind === 'other') return 'another form, which DCS is not asked about'
  // Their vocabulary. A kind added later is shown as it arrived.
  return kind
}

/**
 * How to find a call on Service OCPI's side when there is no audit row id.
 *
 * A timed out call gives this service no body, so no `decisionId` comes back
 * and the link to their audit row cannot be built: exactly the decisions
 * somebody most wants to look up are the ones with no key. Nothing we add to
 * the request can arrive on a response that never came.
 *
 * What is left is the request, and their log is searchable by charge point and
 * time. This service recorded when it asked and how long it waited, so the
 * window to search is [occurred_at, occurred_at + downstream_ms] at that
 * charge point. Written on the page rather than in a runbook, because the
 * person who needs it is reading the row.
 *
 * A correlation header would turn this into one key instead of a window. It is
 * the open `request_correlation` question and not this console's to invent.
 */
export function searchTheirLogFor(
  decision: DecisionSummary,
  downstreamMs: number,
): { from: string; to: string } | null {
  if (!decision.occurred_at) return null
  const asked = new Date(decision.occurred_at)
  if (Number.isNaN(asked.getTime())) return null
  // A floor of one second: a window of zero width is not a window, and their
  // log timestamps are not ours to assume the precision of.
  const waited = Math.max(downstreamMs, 1000)
  return {
    from: asked.toISOString(),
    to: new Date(asked.getTime() + waited).toISOString(),
  }
}

/** Whether this decision is one with no audit row to link to, and why. */
export function hasNoAuditRow(decision: DecisionSummary): boolean {
  // Nothing answered, on the lane Service OCPI serves. Their internal error
  // also sends no id, but it sends a body, so it is their fault with a reason
  // attached rather than a call nobody can find.
  return decision.authority === 'dcs' && decision.basis === 'authority_unavailable'
}

/**
 * What the E4E station list would have said about this charge point.
 *
 * Shadow only: the prefix rule routed the decision in front of the reader, and
 * this sentence is beside it so a disagreement is readable on the row rather
 * than only in an aggregate. Null where there was no list to ask, which is
 * every decision before DEC-22 and every deployment with the catalogue off.
 */
export function catalogueSaysInWords(decision: DecisionSummary): string | null {
  const shadow = decision.catalogue_shadow
  if (!shadow) return null

  // A RETIRED NAME IS SAID, because "we recognised it" and "we recognised it
  // under a name it stopped using in March" are two different sentences and
  // only the second explains why nothing else in the estate looks like this.
  const through = shadow.retired_name ? 'matched the retired name' : 'matched'
  const where = shadow.name
    ? `${through} ${shadow.name}${shadow.namespace ? ` (${shadow.namespace})` : ''}`
    : null

  // EXHAUSTIVE, WITH A NAMED DEFAULT. This was four branches and a
  // fallthrough that said "not in the E4E list", so the fifth state would
  // have rendered as the exact sentence it exists to contradict:
  // `in_list_not_member` means the list HOLDS the station and says it is not
  // ours. Not a crash and not a blank, which is why it was worth finding.
  switch (shadow.catalogue) {
    case 'several':
      // Nothing is chosen, and the page says why rather than showing a station.
      return `${shadow.claimants ?? 2} stations claim this name, so the list cannot answer`
    case 'list_empty':
      return 'the list was empty, which is our own sync and not an answer'
    case 'member':
      return shadow.prefix
        ? `in the E4E list, ${where ?? through}, and the prefix agrees`
        : `in the E4E list, ${where ?? through}, where the prefix says no`
    case 'in_list_not_member':
      // THE LIST HOLDS IT AND SAYS IT IS NOT OURS, which is a fact and not an
      // absence. The mark is named because "not ours" is not a diagnosis: an
      // operator needs to see which rule admitted the station.
      return shadow.prefix
        ? `in the list${viaMark(shadow)} and NOT an E4E station, where the prefix says yes`
        : `in the list${viaMark(shadow)} and not an E4E station, and the prefix agrees`
    case 'not_member':
      return shadow.prefix
        ? 'not in the list at all, where the prefix says yes'
        : 'not in the list at all, and the prefix agrees'
    default:
      // A STATE THIS CONSOLE HAS NO SENTENCE FOR, said rather than guessed.
      // `tsc` makes this unreachable while the union in `api.ts` matches the
      // service, and it is here for the deployment where the console is a
      // release behind the API.
      return `the list answered ${String(shadow.catalogue)}, which this console does not know`
  }
}

/** Which rule admitted the station, in parentheses, or nothing. */
function viaMark(shadow: { membership_via?: string | null }): string {
  return shadow.membership_via ? ` (by ${shadow.membership_via})` : ''
}

/** What a canary authorization is, on the row that marks one (DEC-29). */
export const CANARY_LINE = 'Sent by the canary runner, not a charge point. Owns no session, in no figure.'
